<template>
  <b-row>
    <b-col cols="12">
      <div class="d-flex justify-content-between align-items-center">
        <h3 class="mb-0 font-weight-bolder">Price Promotions</h3>
        <b-link to="/promotions/price-promotions/new">
          <b-button variant="primary" class="d-flex align-items-center">
            <feather-icon icon="PlusIcon" class="mr-1" />
            New Price Promotion
          </b-button>
        </b-link>
      </div>
    </b-col>

    <!-- Info cards -->
    <b-col cols="12" class="mt-1">
      <b-row>
        <b-col md="4">
          <b-card>
            <p class="mb-0">Active Promotions</p>
            <p class="mb-0">3</p>
            <p class="text-muted mb-0">Currently running</p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card>
            <p class="mb-0">Total Exposure</p>
            <p class="mb-0">$250K</p>
            <p class="text-muted mb-0">Across all markets</p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card>
            <p class="mb-0">Avg. Take Rate</p>
            <p class="mb-0">38%</p>
            <p class="text-muted mb-0">Of eligible customers</p>
          </b-card>
        </b-col>
      </b-row>
    </b-col>

    <b-col cols="12">
      <b-card>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Price Promotions</h5>
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="search"
              placeholder="Search promotions..."
              size="sm"
              class="mr-1"
            />
            <b-button variant="outline-secondary" size="sm">
              <feather-icon icon="FilterIcon" />
            </b-button>
          </div>
        </div>

        <!-- Table -->
        <b-table
          :items="items"
          :fields="fields"
          responsive
          :busy="loading"
          show-empty
          :key="tableKey"
          thead-class="text-nowrap"
        >
          <template #empty>
            <div class="text-center text-dark my-2">
              No matching records found
            </div>
          </template>
          <template #cell(created)="{ item }">
            <span>{{ format(new Date(item.startTime), "yyyy-MM-dd") }}</span>
          </template>
          <template #cell(expiry)="{ item }">
            <span class="text-nowrap">{{
              format(new Date(item.endTime), "yyyy-MM-dd")
            }}</span>
          </template>
          <template #cell(refCode)="{ item }">
            <span>{{ item.code }}</span>
          </template>
          <template #cell(market)="{ item }">
            <span>{{ item.market || "-" }}</span>
          </template>
          <template #cell(pricing)="{ item }">
            <span>{{ item.pricing || "-" }}</span>
          </template>
          <template #cell(segments)="{ item }">
            <span
              class="text-nowrap"
              v-for="(target, idx) in item.targets"
              :key="target.id || idx"
              >{{ target.targetType
              }}{{ idx < item.targets.length - 1 ? ", " : "" }}</span
            >
          </template>
          <template #cell(customers)="{ item }">
            <span>{{ item.customers || "-" }}</span>
          </template>
          <template #cell(maxExposure)="{ item }">
            <span>{{ item.globalExposureLimit || "-" }}</span>
          </template>
          <template #cell(status)="data">
            <b-badge
              class="text-capitalize"
              :variant="data.item.status === 'active' ? 'success' : 'warning'"
            >
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                @click="editPromotion(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="openDeleteModal(item)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="d-flex flex-wrap justify-content-end align-items-center">
          <b-pagination
            v-model="page"
            :total-rows="pagination.total"
            :per-page="10"
            first-number
            last-number
          />
        </div>
      </b-card>
    </b-col>

    <b-modal
      id="delete-modal"
      ref="deletePricePromotion"
      title="Confirm Delete Price Promotion"
      centered
      hide-footer
      header-bg-variant="danger"
    >
      <div class="text-center text-body" v-if="selectedPromotion">
        <p>
          Are you sure you want to delete the promotion
          <strong>{{ selectedPromotion.name }}</strong> ?
        </p>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-1" @click="closeDeleteModal">
            Cancel
          </b-button>
          <b-button
            variant="danger"
            @click="confirmDelete"
            :disabled="loadingDelete"
          >
            Delete
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormInput,
  BTable,
  BBadge,
  BPagination,
  BLink,
  BModal,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import { format } from "date-fns";
import { debounce } from "debounce";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BLink,
    BModal,
  },
  data() {
    return {
      search: "",
      page: 1,
      tableKey: 0,
      items: [],
      fields: [
        { key: "created", label: "Date Created" },
        { key: "expiry", label: "Expiry" },
        { key: "name", label: "Promotion Name" },
        { key: "refCode", label: "Ref Code" },
        { key: "market", label: "Market" },
        { key: "pricing", label: "Pricing" },
        { key: "maxExposure", label: "Max Exposure" },
        { key: "customers", label: "# Customers" },
        { key: "segments", label: "Segments" },
        { key: "status", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
      selectedPromotion: null,
    };
  },
  async created() {
    await this.fetchPricePromotions();
  },
  computed: {
    ...mapState("promotions/price-promotions", {
      loading: "loading",
      loadingDelete: "loadingDelete",
      pricePromotions: "pricePromotions",
      pagination: "pagination",
    }),
  },
  watch: {
    page() {
      this.fetchPricePromotions();
    },
    search() {
      this.handleSearchDebounced();
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  methods: {
    ...mapActions("promotions/price-promotions", [
      "getPricePromotions",
      "updatePricePromotion",
      "deletePricePromotion",
    ]),
    format,
    editPromotion(item) {
      this.$router.push(`/promotions/price-promotions/${item.id}`);
    },
    async fetchPricePromotions() {
      if (this.loading) return;
      try {
        const { page, search } = this;
        const params = {
          page,
          type: "Price",
          ...(search && { search }),
        };

        const data = await this.getPricePromotions(params);
        this.items = data;
      } catch (e) {
        this.items = [];
      }
    },
    handleSearch() {
      this.page = 1;
      this.fetchPricePromotions();
    },
    openDeleteModal(item) {
      this.selectedPromotion = item;
      this.$refs.deletePricePromotion.show();
    },
    closeDeleteModal() {
      this.$refs.deletePricePromotion.hide();
      this.selectedPromotion = null;
    },
    async confirmDelete() {
      const res = await this.deletePricePromotion(this.selectedPromotion.id);
      if (!res) return;
      this.closeDeleteModal();
      this.fetchPricePromotions();
    },
  },
};
</script>
